import type {
    AllowMediaChangeOfDirection,
    CallMode,
    LiveCaptionSpeaker,
    ParticipantID,
    RoomID,
    TransferDetails,
} from '@pexip/infinity';
import type {ClientCallType} from '@pexip/infinity/clientCallType';
import type {AudioContentHint, VideoContentHint} from '@pexip/media';
import type {
    BlockedBrowserPermissionsInfoType,
    MeetingId,
} from '@pexip/media-components';
import type {
    CursorCaptureConstraint,
    DisplayCaptureSurfaceType,
    IncludeExcludeConstraint,
    MediaDeviceInfoLike,
} from '@pexip/media-control';
import type {
    Delegate,
    RenderBackend,
    RenderEffects,
} from '@pexip/media-processor';
import type {Signal} from '@pexip/signal';

const VIDEO_STREAM_TRACK_PROCESSOR_API = {
    stream: 'stream',
    canvas: 'canvas',
} as const;

export type VideoStreamTrackProcessorAPIs =
    (typeof VIDEO_STREAM_TRACK_PROCESSOR_API)[keyof typeof VIDEO_STREAM_TRACK_PROCESSOR_API];

export const isVideoStreamTrackProcessorAPIs = (
    t: unknown,
): t is VideoStreamTrackProcessorAPIs => {
    if (typeof t === 'string') {
        return Boolean(
            VIDEO_STREAM_TRACK_PROCESSOR_API[
                t.toLowerCase() as VideoStreamTrackProcessorAPIs
            ],
        );
    }
    return false;
};

export enum PreflightJoinDetailsToRender {
    BlockedDeviceSelection = 'blocked-device-selection',
    DeviceSelection = 'device-selection',
    Spinner = 'spinner',
}

export interface Idp {
    name: string;
    uuid: string;
    redirectUrl?: string;
    img?: string;
    disablePopupFlow?: boolean;
}

export enum FlowType {
    Express = 'express',
    StepByStep = 'step-by-step',
}

export interface BlockedBrowserPermissionsInfo {
    // vp9 is a webm file format
    vp9: string | undefined;
    mp4: string | undefined;
    browserName: string;
    link?: string;
    helpSteps?: Array<{step: number; text: string}>;
    videoTextOverlaySteps?: Array<{
        step: number;
        text: string;
        duration: number;
    }>;
    permissionInfoType: BlockedBrowserPermissionsInfoType;
}

export interface BrowserDetection<T> {
    onChromeOnMobile: () => T;
    onChromeOnDesktop: () => T;
    onFirefoxOnDesktop: () => T;
    onEdgeOnMobile: () => T;
    onEdgeOnDesktop: () => T;
    onSafariOnIPhone: () => T;
    onSafariOnIpad: () => T;
    onSafariOnMacOs: () => T;
    onOtherBrowser: () => T;
}

export type BrowserType =
    | 'chrome-mobile'
    | 'chrome'
    | 'edge'
    | 'edge-macosx'
    | 'firefox'
    | 'safari-ios'
    | 'safari-ipados'
    | 'safari-macosx'
    | 'other';

export interface PreflightTipsInfo {
    step: number;
    text: React.ReactNode;
}

export enum MeetingFlow {
    Loading = 0,
    AreYouHost = 1,
    EnterPin = 2,
    EnterHostPin = 3,
    EnterExtension = 4,
    Idp = 5,
    InMeeting = 6,
    DisplayName = 7,
}

export type TestIdValue = string;
export type GetState<T, K extends keyof T> = () => Pick<T, K>;

export type RegistrationStatus =
    'idle' | 'registering' | 'registered' | 'error';

export type RegistrationStep =
    'register' | 'select-idp' | 'notifications' | 'success' | 'expired' | null;

export enum RegistrationError {
    InvalidVideoAddress = 'invalid-video-address',
    AuthFailed = 'auth-failed',
    RegistrationFailed = 'registration-failed',
    SsoAuthFailed = 'sso-auth-failed',
    SsoUnavailable = 'sso-unavailable',
    UnexpectedError = 'unexpected-error',
}

export enum StepByStepCompletion {
    Uncompleted = 'uncompleted',
    Completed = 'completed',
    Skipped = 'skipped',
    Reviewing = 'reviewing',
}

export type UserMenuStep =
    | 'menu'
    | 'video-and-sound'
    | 'quality'
    | 'meeting-settings'
    | 'layouts'
    | 'accessibility'
    | 'language'
    | 'about'
    | 'stats'
    | 'advanced'
    | 'joining';

export enum ConfirmationModalStates {
    Layout = 'LAYOUT',
    None = 'NONE',
    Unregister = 'UNREGISTER',
}

export enum BrowserCloseConfirmation {
    Unset = 'unset',
    Enabled = 'enabled',
    Disabled = 'disabled',
}

export type AppContextState = {
    isOnStepByStepJoinScreen: boolean;
    isAriaMenuOpen: boolean;
};

export type SetAppContext = <K extends keyof AppContextState>(
    key: K,
    updater: (prevValue: AppContextState[K]) => AppContextState[K],
) => void;

export type AppContextType = {
    appContext: AppContextState;
    setAppContext: SetAppContext;
};

export interface ImageRecord {
    key: string;
    name: string;
    data: Blob;
}

export interface FeedbackConfig {
    enable?: boolean;
    url?: string;
}

export interface CallHistoryConfig {
    enableRecents?: boolean;
    enableFavorites?: boolean;
    favorites?: Array<{
        title: string;
        meetingId: string;
    }>;
}

export interface LiveCaption {
    /** Stable identity of a caption line; kept when an interim line is updated. */
    id: number;
    isFinal: boolean;
    text: string;
    at: Date;
    speakers: LiveCaptionSpeaker[];
}

export enum ParticipantActivities {
    Join = 0,
    Leave = 1,
    Update = 2,
    Transfer = 3,
}

interface BaseParticipantActivity {
    roomId: RoomID;
    participantId: ParticipantID;
}

export interface JoinActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Join;
}
export interface LeaveActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Leave;
}
export interface UpdateActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Update;
}

export type TransferTarget = TransferDetails['target'];
export interface TransferActivity extends BaseParticipantActivity {
    activity: ParticipantActivities.Transfer;
    destinationType: TransferDetails['target'];
    destination: string;
}

export type ParticipantActivity =
    JoinActivity | LeaveActivity | UpdateActivity | TransferActivity;

export type GetSignalType<T> = T extends Signal<unknown, infer R> ? R : never;

export type InternalConfig = {
    asd: boolean;
    audioInput: MediaDeviceInfoLike | undefined;
    audioOutput: MediaDeviceInfoLike | undefined;
    lastFallBackMsg: string;
    registrarNode: string;
    rotateForLargerViewToastCount: number;
    showSelfviewHiddenTooltip: boolean;
    stepByStepCompletion: StepByStepCompletion;
    vad: boolean;
    videoInput: MediaDeviceInfoLike | undefined;
};

export type DefaultUserConfig = {
    allowChangeOfMediaDirection: AllowMediaChangeOfDirection;
    allowVP9: boolean;
    alwaysDisplayUIInterfaces: boolean;
    audioContentHint: AudioContentHint;
    autoGainControl: boolean;
    backgroundBlurAmount: number;
    backgroundThreshold: number;
    bandwidth: string;
    bgImageUrl: string;
    browserCloseConfirmation: BrowserCloseConfirmation;
    callMode: CallMode;
    callType: ClientCallType;
    captureAudio: boolean;
    cursorCapture: CursorCaptureConstraint;
    denoise: boolean;
    deviceAlias: string;
    disableKeyboardShortcuts: boolean;
    displayName: string;
    displaySurface: DisplayCaptureSurfaceType;
    downSampleFactor: number;
    echoCancellation: boolean;
    edgeBlurAmount: number;
    enableBrowserCloseConfirmationByDefault: boolean;
    enforceAudioMute: boolean;
    enforceVideoMute: boolean;
    excludeBystanders: boolean;
    fecc: boolean;
    foregroundThreshold: number;
    gpuAPI: RenderBackend;
    isAudioInputMuted: boolean;
    isGuest: boolean;
    isUserFacing: boolean;
    isVideoInputMuted: boolean;
    join: boolean;
    lightWrapBlurAmount: number;
    lightWrapEdgeBand: number;
    lightWrapIntensity: number;
    lightWrapTightness: number;
    maskCombineRatio: number;
    maxFrameSize?: number;
    mirrorSelfview: boolean;
    morphDilateRadiusPx: number;
    morphErodeRadiusPx: number;
    morphPass: number;
    noiseSuppression: boolean;
    notificationsShowMessagePreview: boolean;
    numberOfReceiveAudioStreams: number;
    numberOfReceiveVideoStreams: number;
    personCenterX: number;
    personCenterY: number;
    preferPresInMix: boolean;
    presoContentHint: VideoContentHint;
    segmentationEffects: RenderEffects;
    showParticipantAuthenticatedState: boolean;
    showSelfView: boolean;
    sigmaRangeHi: number;
    sigmaRangeLo: number;
    sigmaSpace: number;
    skipDisplayNamePrompt: boolean;
    stopCameraAsMute: boolean;
    surfaceSwitching: IncludeExcludeConstraint;
    userBgImageAssets: string[];
    videoContentHint: VideoContentHint;
    videoProcessingAPI: VideoStreamTrackProcessorAPIs;
    videoProcessingDelegate: Delegate;
    /*
     * The reason why favoritesOrder is stored as an object rather than array is because
     * config-manager uses mergeSortArray on arrays stored as props on the config object
     * and that alphabetically sorts the array which rearranges the user intended order.
     * mergeSortArray is not executed on arrays inside objects.
     */
    favoritesOrder: {meetingIds: MeetingId[]};
    autoPictureInPicture: boolean;
};

export type AppConfig = DefaultUserConfig & InternalConfig;

export interface MediaDirection {
    send?: boolean;
    recv?: boolean;
}
export interface CallTypeInput {
    audio?: MediaDirection | boolean;
    video?: MediaDirection | boolean;
    presentation?: MediaDirection | boolean;
}

/**
 * In-meeting participants of a single room the current user has no direct
 * chat with yet.
 */
export interface RoomParticipantsWithNoDirectChats {
    /** The room the participants are currently in. */
    roomId: RoomID;
    /** Participants a new direct chat can be started with. */
    participantsWithNoDirectChats: ParticipantID[];
    /** Participants whose client does not support direct chat. */
    unsupportedParticipantsWithNoDirectChats: ParticipantID[];
}
